/**
 * Pure-function citation pre-processing for the Markdown component.
 *
 * Extracted from `Markdown.tsx` so unit tests can import without pulling in
 * `react-markdown` (which is published as ESM-only and Jest can't transform
 * without extra config).
 */

/**
 * Escape HTML special chars in a string so it can safely be inserted into raw
 * HTML markup that flows through ReactMarkdown's `rehypeRaw` plugin.
 *
 * Citation labels and ids originate from tenant-controlled data — Tableau
 * dashboard / user names are author-defined and may contain `<`, `>`, `&`,
 * `"`, `'`. Without escaping, `rehypeRaw` parses them as live HTML and React
 * renders the resulting elements (e.g. `<img onerror=...>`, `<a href="javascript:...">`).
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Convert `[[type:id:label]]` citations to HTML spans for ReactMarkdown rendering.
 *
 * Tenant-controlled values (label, id, type) flow into raw HTML, so all three
 * are escaped to prevent HTML/script injection via Tableau-author-defined
 * dashboard / user names.
 */
export function processCitations(content: string): string {
  return content.replace(/\[\[(\w+):([^:\]]+)(?::([^\]]+))?\]\]/g, (_, type, id, label) => {
    const displayLabel = label || id;
    const safeType = escapeHtml(type);
    const safeId = escapeHtml(id);
    const safeLabel = escapeHtml(displayLabel);
    return `<span class="citation-link" data-citation-type="${safeType}" data-citation-id="${safeId}">${safeLabel}</span>`;
  });
}
